import type { DateRange } from '@/data/types'
import type * as api from '@/data/api'

export const keys = {
  session: ['session'] as const,
  createParent: ['create-parent'] as const,
  account: (id: string) => ['account', id] as const,
  upline: (id: string) => ['upline', id] as const,
  downline: (query: api.DownlineQuery) => ['downline', query] as const,
  children: (id: string) => ['children', id] as const,
  /**
   * Keyed on the whole query. The overview grew a scope, a sport list and a game
   * list; the old `['overview', from, to]` key would have served a
   * Cricket-filtered snapshot to an unfiltered request and the page would have
   * silently lied.
   */
  overview: (query: api.OverviewQuery) => ['overview', query] as const,
  /** Whole-query key, for the same reason `overview` is: scope and the sport and
   *  game lists all change the numbers. */
  turnover: (query: api.OverviewQuery) => ['turnover', query] as const,
  /** Whole-object key, so every filter and the sort participate automatically. */
  exposure: (query: api.ExposureQuery) => ['exposure', query] as const,
  /**
   * Basis is IN the key. Without it, flipping My share ↔ Total book serves the
   * cached numbers computed under the other basis — the worst possible cache bug
   * on a money screen, because both values are plausible.
   */
  marketBook: (id: string, basis: string) => ['market-book', id, basis] as const,
  /**
   * Nested under the market-book prefix on purpose: one
   * `invalidateQueries({ queryKey: ['market-book'] })` then clears the root and
   * every expanded branch together, so a parent row and its children can never
   * be a poll apart.
   */
  marketBookBranch: (id: string, parentId: string, basis: string) =>
    ['market-book', id, basis, 'branch', parentId] as const,
  pnl: (query: api.PnlQuery) => ['pnl', query] as const,
  memberPnl: (query: api.PnlQuery) => ['member-pnl', query] as const,
  memberDetail: (id: string, range: DateRange) =>
    ['member-detail', id, range.from, range.to] as const,
  bets: (query: api.BetQuery) => ['bets', query] as const,
  transfers: (query: api.TransferQuery) => ['transfers', query] as const,
  commission: (query: api.PnlQuery) => ['commission', query] as const,
  statement: (id: string, range?: DateRange) =>
    ['statement', id, range?.from, range?.to] as const,
  activity: (limit: number) => ['activity', limit] as const,
  /** Blast radius for stopping one account — read before the confirm, never cached long. */
  stopImpact: (id: string) => ['stop-impact', id] as const,
  search: (query: api.SearchQuery) => ['search', query] as const,
  usernameAvailable: (username: string) => ['username-available', username] as const,
  mfaStatus: ['mfa-status'] as const,
}
